import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { C } from '../styles';
import { changePassword, verifySession } from '../services/api';
import SalesOverview from './tabs/SalesOverview';
import StaffPerformance from './tabs/StaffPerformance';
import PaymentAnalysis from './tabs/PaymentAnalysis';
import CustomerInsights from './tabs/CustomerInsights';
import AppointmentTracker from './tabs/AppointmentTracker';
import TeamManagement from './tabs/TeamManagement';
import BranchComparison from './tabs/BranchComparison';
import ReportBuilder from './ReportBuilder';
import SuperadminPanel from './SuperadminPanel';

function Icon({ d, active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d={d} stroke={active ? C.accent : 'rgba(255,255,255,0.65)'} strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function SalesIco({ active }) { return <Icon active={active} d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"/>; }
function StaffIco({ active }) { return <Icon active={active} d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>; }
function PayIco({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="1" y="4" width="22" height="16" rx="3" stroke={active ? C.accent : 'rgba(255,255,255,0.65)'} strokeWidth="1.8"/>
      <path d="M1 10h22" stroke={active ? C.accent : 'rgba(255,255,255,0.65)'} strokeWidth="1.8"/>
    </svg>
  );
}
function CustIco({ active }) { return <Icon active={active} d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/>; }
function BookIco({ active }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="18" rx="2" stroke={active ? C.accent : 'rgba(255,255,255,0.65)'} strokeWidth="1.8"/>
      <path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01" stroke={active ? C.accent : 'rgba(255,255,255,0.65)'} strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}
function TeamIco({ active }) { return <Icon active={active} d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87"/>; }
function BranchIco({ active }) { return <Icon active={active} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2zM9 22V12h6v10"/>; }

// ── Change Password Modal ──────────────────────────────────────────────────
const ChangePasswordModal = ({ onClose }) => {
  const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
      setError('All fields are required');
      return;
    }
    if (form.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await changePassword({ old_password: form.oldPassword, new_password: form.newPassword });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error('Change password error:', err);
      console.error('Response:', err.response);
      const errMsg = err.response?.data?.error || err.message || 'Failed to change password';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modalBox}>
        <div style={S.modalHeader}>
          <p style={S.modalTitle}>Change Password</p>
          <button onClick={onClose} style={S.modalClose}>×</button>
        </div>

        {success ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
            <p style={{ fontSize: '15px', fontWeight: '600', color: C.accent }}>Password changed successfully</p>
          </div>
        ) : (
          <>
            <div style={S.modalField}>
              <label style={S.modalLabel}>Current Password</label>
              <input
                type="password"
                style={S.modalInput}
                value={form.oldPassword}
                onChange={e => setForm({ ...form, oldPassword: e.target.value })}
                placeholder="Enter current password"
              />
            </div>

            <div style={S.modalField}>
              <label style={S.modalLabel}>New Password</label>
              <input
                type="password"
                style={S.modalInput}
                value={form.newPassword}
                onChange={e => setForm({ ...form, newPassword: e.target.value })}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div style={S.modalField}>
              <label style={S.modalLabel}>Confirm New Password</label>
              <input
                type="password"
                style={S.modalInput}
                value={form.confirmPassword}
                onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                placeholder="Re-enter new password"
              />
            </div>

            {error && <div style={S.modalError}>{error}</div>}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={handleSubmit} disabled={loading} style={S.modalBtnPrimary}>
                {loading ? 'Changing...' : 'Change Password'}
              </button>
              <button onClick={onClose} style={S.modalBtnSecondary}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main Dashboard Component ───────────────────────────────────────────────
const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('ccs_view') || 'phone');

  const toggleView = () => {
    const next = viewMode === 'phone' ? 'tablet' : 'phone';
    setViewMode(next);
    localStorage.setItem('ccs_view', next);
  };

  const maxW = viewMode === 'tablet' ? '820px' : '480px';

  // ── Session Verification (Bug #1) ────────────────────────────────────────
  // Check every 2 minutes if user is still active
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await verifySession();
        if (!res.data.valid) {
          alert('Your account has been deactivated. You will be logged out.');
          logout();
        }
      } catch (err) {
        // If verify endpoint fails with 401, user is already logged out via interceptor
        console.error('Session check failed:', err);
      }
    };

    const interval = setInterval(checkSession, 2 * 60 * 1000); // Every 2 minutes
    return () => clearInterval(interval);
  }, [logout]);

  // ── Superadmin gets their own full PC panel
  if (user?.role === 'superadmin') {
    return <SuperadminPanel onLogout={logout} />;
  }

  const isBoss    = user?.role === 'boss';
  const isManager = ['branch_manager', 'area_manager'].includes(user?.role);
  const canSeeBranches = ['boss', 'area_manager', 'superadmin'].includes(user?.role);

  // Boss sees Team tab, others don't
  const NAV_TABS = [
    { path: '/',             label: 'Sales',    icon: SalesIco  },
    { path: '/staff',        label: 'Staff',    icon: StaffIco  },
    { path: '/payments',     label: 'Payments', icon: PayIco    },
    { path: '/customers',    label: 'Customers',icon: CustIco   },
    { path: '/appointments', label: 'Bookings', icon: BookIco   },
    ...(canSeeBranches ? [{ path: '/branches', label: 'Branches', icon: BranchIco }] : []),
    ...(isBoss ? [{ path: '/team', label: 'Team', icon: TeamIco }] : []),
  ];

  const tabLabels = {
    '/':             'Sales Overview',
    '/staff':        'Staff Performance',
    '/payments':     'Payment Analysis',
    '/customers':    'Customer Insights',
    '/appointments': 'Appointment Tracker',
    '/branches':     'Branch Comparison',
    '/team':         'Team Management',
    '/reports':      'Report Builder',
    '/appointments': 'Appointment Tracker',
    '/team':         'Team Management',
    '/reports':      'Report Builder',
  };

  const isActive = (p) => location.pathname === p;

  return (
    <div style={{ ...S.container, maxWidth: maxW }}>

      {/* HEADER */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <img src="/logo512.png" alt="CCS" style={S.logoMark} />
          <div>
            <p style={S.headerTitle}>{tabLabels[location.pathname] || 'CCS BI Dashboard'}</p>
            <p style={S.headerSub}>{user?.tenant_id} · {user?.full_name}</p>
          </div>
        </div>
        <div style={S.headerRight}>
          <span style={S.roleBadge}>{user?.role}</span>
          <button onClick={toggleView} title={viewMode === 'phone' ? 'Switch to Tablet view' : 'Switch to Phone view'}
            style={{ ...S.menuBtn, padding:'6px 10px', gap:'4px', fontSize:'11px', fontWeight:'600', color: C.textSec }}>
            {viewMode === 'phone'
              ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="5" y="2" width="14" height="20" rx="2" stroke={C.textMuted} strokeWidth="1.8"/><circle cx="12" cy="18" r="1" fill={C.textMuted}/></svg>
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="14" rx="2" stroke={C.accent} strokeWidth="1.8"/><circle cx="19" cy="13" r="1" fill={C.accent}/></svg>
            }
          </button>
          <button onClick={() => setMenuOpen(!menuOpen)} style={S.menuBtn}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="5"  r="1.6" fill="white"/>
              <circle cx="12" cy="12" r="1.6" fill="white"/>
              <circle cx="12" cy="19" r="1.6" fill="white"/>
            </svg>
          </button>
        </div>

        {menuOpen && (
          <div style={S.dropdown}>
            {(isBoss || isManager) && (
              <>
                <button style={S.dropItem} onClick={() => { navigate('/reports'); setMenuOpen(false); }}>
                  {isBoss ? '📊  Report Builder' : '📊  My Reports'}
                </button>
                <div style={S.dropDivider}/>
              </>
            )}
            <button style={S.dropItem} onClick={() => { setShowPasswordModal(true); setMenuOpen(false); }}>
              🔑  Change Password
            </button>
            <div style={S.dropDivider}/>
            <button style={{ ...S.dropItem, color: C.danger }} onClick={logout}>
              Sign Out
            </button>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div style={S.content} onClick={() => setMenuOpen(false)}>
        <Routes>
          <Route path="/"             element={<SalesOverview />} />
          <Route path="/staff"        element={<StaffPerformance />} />
          <Route path="/payments"     element={<PaymentAnalysis />} />
          <Route path="/customers"    element={<CustomerInsights />} />
          <Route path="/appointments" element={<AppointmentTracker />} />
          <Route path="/branches"     element={<BranchComparison />} />
          <Route path="/team"         element={<TeamManagement />} />
          <Route path="/reports"      element={<ReportBuilder />} />
        </Routes>
      </div>

      {/* BOTTOM NAV */}
      <div style={{ ...S.nav, maxWidth: maxW, width: '100%' }}>
        {NAV_TABS.map(tab => {
          const active = isActive(tab.path);
          const IconComp = tab.icon;
          return (
            <button key={tab.path} onClick={() => navigate(tab.path)} style={S.navItem}>
              <div style={{ ...S.navIconWrap, background: active ? C.accentDim : 'transparent' }}>
                <IconComp active={active}/>
              </div>
              <span style={{ ...S.navLabel, color: active ? C.accent : 'rgba(255,255,255,0.65)' }}>
                {tab.label}
              </span>
              {active && <div style={S.navDot}/>}
            </button>
          );
        })}
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />}

    </div>
  );
};

const S = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: C.bgBase,
    maxWidth: '480px',
    margin: '0 auto',
    position: 'relative',
  },
  header: {
    background: C.bgCard,
    padding: '10px 10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    borderBottom: `1px solid ${C.border}`,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  logoMark: {
    width: '36px', height: '36px',
    borderRadius: '50%',
    objectFit: 'contain',
  },
  headerTitle: { fontSize: '15px', fontWeight: '700', color: C.textPrimary, margin: 0 },
  headerSub:   { fontSize: '10px', color: C.textMuted, margin: '1px 0 0' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  roleBadge: {
    background: C.bgElevated, color: C.textSec,
    fontSize: '10px', fontWeight: '600',
    padding: '3px 10px', borderRadius: '20px',
    textTransform: 'capitalize',
    border: `1px solid ${C.border}`,
  },
  menuBtn: {
    background: C.bgElevated, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '7px',
    cursor: 'pointer', display: 'flex', alignItems: 'center',
  },
  dropdown: {
    position: 'absolute', top: '62px', right: '16px',
    background: C.bgSurface,
    borderRadius: '14px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    border: `1px solid ${C.border}`,
    overflow: 'hidden', minWidth: '170px', zIndex: 200,
  },
  dropItem: {
    display: 'block', width: '100%',
    padding: '13px 16px',
    background: 'none', border: 'none',
    textAlign: 'left', fontSize: '13px',
    color: C.textPrimary, cursor: 'pointer', fontWeight: '500',
  },
  dropDivider: { height: '1px', background: C.border },
  content: { flex: 1, overflowY: 'auto', paddingBottom: '80px' },
  nav: {
    position: 'fixed', bottom: 0,
    left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: '480px',
    background: C.bgCard,
    display: 'flex',
    borderTop: `1px solid ${C.border}`,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
    zIndex: 100,
  },
  navItem: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '8px 4px 6px',
    background: 'none', border: 'none', cursor: 'pointer', gap: '3px',
  },
  navIconWrap: {
    width: '36px', height: '28px',
    borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  navLabel:  { fontSize: '9px', fontWeight: '500', letterSpacing: '0.2px' },
  navDot:    { width: '4px', height: '4px', borderRadius: '50%', background: C.accent },

  // Modal styles
  modal: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 300,
  },
  modalBox: {
    background: C.bgSurface,
    borderRadius: '16px',
    border: `1px solid ${C.border}`,
    padding: '24px',
    width: '90%',
    maxWidth: '400px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: C.textPrimary,
    margin: 0,
  },
  modalClose: {
    background: 'none',
    border: 'none',
    color: C.textMuted,
    fontSize: '24px',
    cursor: 'pointer',
    lineHeight: 1,
  },
  modalField: {
    marginBottom: '16px',
  },
  modalLabel: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    color: C.textMuted,
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  modalInput: {
    width: '100%',
    padding: '11px 14px',
    background: C.bgElevated,
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
    fontSize: '14px',
    color: C.textPrimary,
    outline: 'none',
    boxSizing: 'border-box',
  },
  modalError: {
    color: C.danger,
    fontSize: '13px',
    marginTop: '12px',
    padding: '8px 12px',
    background: 'rgba(255,107,107,0.1)',
    borderRadius: '8px',
    border: `1px solid ${C.danger}`,
  },
  modalBtnPrimary: {
    flex: 1,
    padding: '12px',
    background: C.accent,
    color: '#000',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  modalBtnSecondary: {
    padding: '12px 20px',
    background: C.bgElevated,
    color: C.textSec,
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default Dashboard;